/**
 * Messaging Service
 * Handles all messaging-related Firestore operations
 */

import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  writeBatch,
  Timestamp,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from './config';
import { handleFirebaseAuthError } from '@/utils/firebaseErrorHandler';
import type {
  Conversation,
  Message,
  MessageReaction,
  MessageTranscription,
  ConversationParticipant,
  ConversationUser,
  MessagingResult,
  ConversationType,
} from '@/types/messaging';
import { userService } from './database';
import { fetchUserRoles } from '@/utils/roleHelpers';

/**
 * Helper function to check if Firebase is configured
 */
const checkFirebase = () => {
  if (!db) {
    return { success: false, error: 'Firebase not configured' };
  }
  return { success: true };
};

/**
 * Get user display info (name, email, picture) for denormalization
 */
const getUserDisplayInfo = async (userId: string): Promise<{ name: string; email: string; picture?: string }> => {
  try {
    const userResult = await userService.getUser(userId);
    if (userResult.success && userResult.data) {
      return {
        name: userResult.data.name || userResult.data.email || 'Unknown User',
        email: userResult.data.email || '',
        picture: userResult.data.picture,
      };
    }
  } catch (error) {
    console.error('Error fetching user info:', error);
  }
  return { name: 'Unknown User', email: '' };
};

/**
 * Create conversation participant document
 */
const createConversationParticipant = async (
  conversationId: string,
  userId: string,
  organizationId: string,
  role: 'member' | 'admin' = 'member'
): Promise<void> => {
  try {
    const participantId = `${conversationId}_${userId}`;
    const participantRef = doc(db, 'conversationParticipants', participantId);
    
    await setDoc(participantRef, {
      conversationId,
      userId,
      organizationId,
      unreadCount: 0,
      joinedAt: serverTimestamp(),
      role,
    }, { merge: true });
  } catch (error) {
    console.error('Error creating conversation participant:', error);
    throw error;
  }
};

/**
 * Sync participant documents for a conversation
 * Ensures all participants in the array have corresponding participant documents
 * This fixes the bug where users are in participants array but don't have participant documents
 */
const syncConversationParticipantDocuments = async (
  conversation: Conversation
): Promise<void> => {
  try {
    if (!conversation.participants || conversation.participants.length === 0) {
      return;
    }

    // Check which participants have documents
    const participantIds = conversation.participants;
    const participantChecks = participantIds.map(async (userId) => {
      const participantId = `${conversation.id}_${userId}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);
      const participantSnap = await getDoc(participantRef);
      return { userId, exists: participantSnap.exists() };
    });

    const results = await Promise.all(participantChecks);
    const missingParticipants = results
      .filter(r => !r.exists)
      .map(r => r.userId);

    // Create missing participant documents
    if (missingParticipants.length > 0) {
      console.log(`[syncConversationParticipantDocuments] Creating ${missingParticipants.length} missing participant documents for conversation ${conversation.id}`);
      
      const batch = writeBatch(db);
      for (const userId of missingParticipants) {
        const participantId = `${conversation.id}_${userId}`;
        const participantRef = doc(db, 'conversationParticipants', participantId);
        
        // Determine role - check if user is the creator or if they have an existing role
        let participantRole: 'member' | 'admin' = 'member';
        if (conversation.createdBy === userId) {
          participantRole = 'admin';
        }
        
        batch.set(participantRef, {
          conversationId: conversation.id,
          userId,
          organizationId: conversation.organizationId,
          unreadCount: 0,
          joinedAt: serverTimestamp(),
          role: participantRole,
        });
      }
      
      await batch.commit();
      console.log(`[syncConversationParticipantDocuments] ✅ Created ${missingParticipants.length} missing participant documents`);
    }
  } catch (error) {
    console.error('[syncConversationParticipantDocuments] Error syncing participant documents:', error);
    // Don't throw - this is a repair function, shouldn't break main flow
  }
};

/**
 * Messaging Service
 */
export const messagingService = {
  /**
   * Create a direct conversation between two users
   */
  createDirectConversation: async (
    userId1: string,
    userId2: string,
    organizationId: string
  ): Promise<MessagingResult<Conversation>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      // Validate users are in the same organization
      const [user1Result, user2Result] = await Promise.all([
        userService.getUser(userId1),
        userService.getUser(userId2),
      ]);

      if (!user1Result.success || !user1Result.data || !user2Result.success || !user2Result.data) {
        return { success: false, error: 'One or both users not found' };
      }

      // Check if conversation already exists
      const conversationsRef = collection(db, 'conversations');
      const existingQuery = query(
        conversationsRef,
        where('type', '==', 'direct'),
        where('organizationId', '==', organizationId),
        where('participants', '==', [userId1, userId2].sort())
      );

      const existingSnapshot = await getDocs(existingQuery);
      if (!existingSnapshot.empty) {
        const existingConv = existingSnapshot.docs[0];
        return {
          success: true,
          data: { id: existingConv.id, ...existingConv.data() } as Conversation,
        };
      }

      // Create new conversation
      const conversationData: Omit<Conversation, 'id'> = {
        type: 'direct',
        organizationId,
        participants: [userId1, userId2].sort(),
        createdBy: userId1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const conversationRef = await addDoc(conversationsRef, conversationData);
      
      // Create participant documents for both users
      await Promise.all([
        createConversationParticipant(conversationRef.id, userId1, organizationId),
        createConversationParticipant(conversationRef.id, userId2, organizationId),
      ]);

      return {
        success: true,
        data: { id: conversationRef.id, ...conversationData } as Conversation,
      };
    } catch (error: any) {
      console.error('Error creating direct conversation:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to create conversation' };
    }
  },

  /**
   * Create a group conversation (only managers/admins can create)
   */
  createGroupConversation: async (
    name: string,
    description: string | undefined,
    memberIds: string[],
    organizationId: string,
    createdBy: string,
    conversationType: ConversationType = 'group'
  ): Promise<MessagingResult<Conversation>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      // Validate all members are in the same organization
      const memberChecks = await Promise.all(
        memberIds.map(id => userService.getUser(id))
      );

      const invalidMembers = memberChecks.filter(result => !result.success || !result.data);
      if (invalidMembers.length > 0) {
        return { success: false, error: 'Some members not found' };
      }

      // Create conversation with specified type
      const conversationsRef = collection(db, 'conversations');
      const conversationData: any = {
        type: conversationType,
        organizationId,
        participants: [...new Set([createdBy, ...memberIds])], // Ensure creator is included
        name: name.trim(),
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Only include description if it has a value (Firestore doesn't allow undefined)
      if (description && description.trim()) {
        conversationData.description = description.trim();
      }

      const conversationRef = await addDoc(conversationsRef, conversationData);

      // Create participant documents for all members
      const participantPromises = conversationData.participants.map((userId: string) =>
        createConversationParticipant(
          conversationRef.id,
          userId,
          organizationId,
          userId === createdBy ? 'admin' : 'member'
        )
      );

      await Promise.all(participantPromises);

      return {
        success: true,
        data: { id: conversationRef.id, ...conversationData } as Conversation,
      };
    } catch (error: any) {
      console.error('Error creating group conversation:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to create group' };
    }
  },

  /**
   * Send a message to a conversation
   */
  sendMessage: async (
    conversationId: string,
    text: string,
    sentBy: string,
    attachments?: Message['attachments'],
    transcription?: MessageTranscription,
    mentions?: Message['mentions']
  ): Promise<MessagingResult<Message>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      // Get conversation to validate and get organizationId
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        return { success: false, error: 'Conversation not found' };
      }

      const conversation = { id: conversationSnap.id, ...conversationSnap.data() } as Conversation;

      // Validate user is a participant
      if (!conversation.participants.includes(sentBy)) {
        return { success: false, error: 'You are not a participant in this conversation' };
      }

      // Get user display info
      const userInfo = await getUserDisplayInfo(sentBy);

      // Create message
      const messagesRef = collection(db, 'messages');
      const messageData: any = {
        conversationId,
        sentBy,
        sentByName: userInfo.name,
        text: text.trim(),
        organizationId: conversation.organizationId,
        createdAt: serverTimestamp(),
      };

      // Only include attachments if provided (Firestore doesn't allow undefined)
      if (attachments && attachments.length > 0) {
        messageData.attachments = attachments;
      }

      // Only include transcription if provided (Firestore doesn't allow undefined)
      if (transcription) {
        messageData.transcription = transcription;
      }

      // Only include mentions if provided (Firestore doesn't allow undefined)
      if (mentions && mentions.length > 0) {
        messageData.mentions = mentions;
      }

      const messageRef = await addDoc(messagesRef, messageData);

      // Update conversation lastMessage and updatedAt
      await updateDoc(conversationRef, {
        lastMessage: {
          text: text.trim(),
          sentBy,
          sentByName: userInfo.name,
          sentAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });

      // Update unread counts for all participants except sender
      // Also restore deleted conversations when new messages arrive
      // AND create missing participant documents for users in participants array
      const batch = writeBatch(db);
      const { deleteField } = await import('firebase/firestore');
      const participantPromises = conversation.participants
        .filter(userId => userId !== sentBy)
        .map(async (userId) => {
          const participantId = `${conversationId}_${userId}`;
          const participantRef = doc(db, 'conversationParticipants', participantId);
          const participantSnap = await getDoc(participantRef);
          
          if (participantSnap.exists()) {
            const participantData = participantSnap.data();
            const currentCount = participantData.unreadCount || 0;
            const updates: any = {
              unreadCount: currentCount + 1,
            };
            
            // Restore conversation if it was deleted (remove deletedAt field)
            // This makes deleted conversations reappear when new messages arrive
            if (participantData.deletedAt) {
              updates.deletedAt = deleteField();
              console.log(`[sendMessage] Restoring deleted conversation ${conversationId} for user ${userId}`);
            }
            
            batch.update(participantRef, updates);
          } else {
            // Create participant if doesn't exist
            // This fixes orphaned participants - users in participants array but missing participant documents
            // When a new message arrives, the conversation will reappear for them
            const participantRole = (conversation.createdBy === userId) ? 'admin' : 'member';
            batch.set(participantRef, {
              conversationId,
              userId,
              organizationId: conversation.organizationId,
              unreadCount: 1,
              joinedAt: serverTimestamp(),
              role: participantRole,
            });
            console.log(`[sendMessage] Creating missing participant document for user ${userId} in conversation ${conversationId}`);
          }
        });

      await Promise.all(participantPromises);
      await batch.commit();

      return {
        success: true,
        data: { id: messageRef.id, ...messageData } as Message,
      };
    } catch (error: any) {
      console.error('Error sending message:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to send message' };
    }
  },

  /**
   * Get all conversations for a user in an organization
   */
  getConversations: async (
    userId: string,
    organizationId: string
  ): Promise<MessagingResult<Conversation[]>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      // Get all participant records for this user
      const participantsRef = collection(db, 'conversationParticipants');
      const participantsQuery = query(
        participantsRef,
        where('userId', '==', userId),
        where('organizationId', '==', organizationId)
      );

      const participantsSnapshot = await getDocs(participantsQuery);
      // Filter out deleted conversations (where deletedAt is set)
      const conversationIds = participantsSnapshot.docs
        .filter(doc => {
          const data = doc.data();
          return !data.deletedAt; // Only include conversations that are not deleted
        })
        .map(doc => doc.data().conversationId);

      if (conversationIds.length === 0) {
        return { success: true, data: [] };
      }

      // Fetch conversations (in batches of 10 due to Firestore 'in' query limit)
      const conversations: Conversation[] = [];
      const batchSize = 10;

      for (let i = 0; i < conversationIds.length; i += batchSize) {
        const batch = conversationIds.slice(i, i + batchSize);
        const conversationPromises = batch.map(async (convId) => {
          const convRef = doc(db, 'conversations', convId);
          const convSnap = await getDoc(convRef);
          if (convSnap.exists()) {
            return { id: convSnap.id, ...convSnap.data() } as Conversation;
          }
          return null;
        });

        const batchResults = await Promise.all(conversationPromises);
        conversations.push(...batchResults.filter((c): c is Conversation => c !== null));
      }

      // Sort by updatedAt (most recent first)
      conversations.sort((a, b) => {
        const aTime = a.updatedAt?.toMillis?.() || 
                      (a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0) ||
                      (a.lastMessage?.sentAt?.toMillis?.() || 0);
        const bTime = b.updatedAt?.toMillis?.() || 
                      (b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0) ||
                      (b.lastMessage?.sentAt?.toMillis?.() || 0);
        return bTime - aTime;
      });

      // Sync participant documents for all conversations to fix orphaned participants
      // Do this asynchronously so it doesn't block the main flow
      Promise.all(conversations.map(conv => syncConversationParticipantDocuments(conv)))
        .catch(err => console.error('[getConversations] Error syncing participant documents:', err));

      return { success: true, data: conversations };
    } catch (error: any) {
      console.error('Error getting conversations:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to get conversations' };
    }
  },

  /**
   * Get messages for a conversation (paginated)
   */
  getMessages: async (
    conversationId: string,
    limitCount: number = 50,
    beforeTimestamp?: any // Firestore Timestamp or Date to paginate before
  ): Promise<MessagingResult<Message[]>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const messagesRef = collection(db, 'messages');
      let messagesQuery;

      // If beforeTimestamp is provided, paginate to get older messages
      if (beforeTimestamp) {
        // Convert to Firestore Timestamp if needed
        let timestampValue = beforeTimestamp;
        if (beforeTimestamp.toDate) {
          // It's already a Firestore Timestamp
          timestampValue = beforeTimestamp;
        } else if (beforeTimestamp instanceof Date) {
          // Convert Date to Firestore Timestamp
          timestampValue = Timestamp.fromDate(beforeTimestamp);
        } else if (typeof beforeTimestamp === 'number') {
          // Convert milliseconds to Firestore Timestamp
          timestampValue = Timestamp.fromMillis(beforeTimestamp);
        }

        // Query for messages before the given timestamp (older messages)
        // Since we're ordering by createdAt desc, messages with createdAt < timestampValue are older
        messagesQuery = query(
          messagesRef,
          where('conversationId', '==', conversationId),
          where('createdAt', '<', timestampValue),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      } else {
        // Initial load - get most recent messages
        messagesQuery = query(
          messagesRef,
          where('conversationId', '==', conversationId),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      }

      const messagesSnapshot = await getDocs(messagesQuery);
      const messages: Message[] = [];

      messagesSnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() } as Message);
      });

      // Reverse to show oldest first
      messages.reverse();

      return { success: true, data: messages };
    } catch (error: any) {
      console.error('Error getting messages:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to get messages' };
    }
  },

  /**
   * Mark conversation as read for a user
   */
  markAsRead: async (
    conversationId: string,
    userId: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const participantId = `${conversationId}_${userId}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);

      await updateDoc(participantRef, {
        lastReadAt: serverTimestamp(),
        unreadCount: 0,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error marking conversation as read:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to mark as read' };
    }
  },

  /**
   * Get unread count for a user in an organization
   */
  getUnreadCount: async (
    userId: string,
    organizationId: string
  ): Promise<MessagingResult<number>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const participantsRef = collection(db, 'conversationParticipants');
      const participantsQuery = query(
        participantsRef,
        where('userId', '==', userId),
        where('organizationId', '==', organizationId)
      );

      const participantsSnapshot = await getDocs(participantsQuery);
      let totalUnread = 0;

      participantsSnapshot.forEach((doc) => {
        const unreadCount = doc.data().unreadCount || 0;
        totalUnread += unreadCount;
      });

      return { success: true, data: totalUnread };
    } catch (error: any) {
      console.error('Error getting unread count:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to get unread count' };
    }
  },

  /**
   * Get all conversations for a user across multiple organizations
   */
  getConversationsForOrganizations: async (
    userId: string,
    organizationIds: string[]
  ): Promise<MessagingResult<Conversation[]>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) return firebaseCheck;
    if (!organizationIds.length) return { success: true, data: [] };

    try {
      const participantsRef = collection(db, 'conversationParticipants');
      const allConversationIds = new Set<string>();
      const chunkSize = 10; // Firestore 'in' limit

      for (let i = 0; i < organizationIds.length; i += chunkSize) {
        const chunk = organizationIds.slice(i, i + chunkSize);
        const q = query(
          participantsRef,
          where('userId', '==', userId),
          where('organizationId', 'in', chunk)
        );
        const snapshot = await getDocs(q);
        snapshot.docs.forEach((d) => {
          const data = d.data();
          if (!data.deletedAt) allConversationIds.add(data.conversationId);
        });
      }

      if (allConversationIds.size === 0) return { success: true, data: [] };

      const conversations: Conversation[] = [];
      const batchSize = 10;
      const ids = Array.from(allConversationIds);
      for (let i = 0; i < ids.length; i += batchSize) {
        const batch = ids.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(async (convId) => {
            const convRef = doc(db, 'conversations', convId);
            const snap = await getDoc(convRef);
            if (snap.exists()) return { id: snap.id, ...snap.data() } as Conversation;
            return null;
          })
        );
        conversations.push(...results.filter((c): c is Conversation => c !== null));
      }

      conversations.sort((a, b) => {
        const aTime = a.updatedAt?.toMillis?.() || (a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0) || (a.lastMessage?.sentAt?.toMillis?.() || 0);
        const bTime = b.updatedAt?.toMillis?.() || (b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0) || (b.lastMessage?.sentAt?.toMillis?.() || 0);
        return bTime - aTime;
      });

      Promise.all(conversations.map((conv) => syncConversationParticipantDocuments(conv))).catch((err) =>
        console.error('[getConversationsForOrganizations] Error syncing participant documents:', err)
      );
      return { success: true, data: conversations };
    } catch (error: any) {
      console.error('Error getting conversations for organizations:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to get conversations' };
    }
  },

  /**
   * Get total unread count for a user across multiple organizations
   */
  getUnreadCountAllOrgs: async (
    userId: string,
    organizationIds: string[]
  ): Promise<MessagingResult<number>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) return firebaseCheck;
    if (!organizationIds.length) return { success: true, data: 0 };

    try {
      const participantsRef = collection(db, 'conversationParticipants');
      let totalUnread = 0;
      const chunkSize = 10;

      for (let i = 0; i < organizationIds.length; i += chunkSize) {
        const chunk = organizationIds.slice(i, i + chunkSize);
        const q = query(
          participantsRef,
          where('userId', '==', userId),
          where('organizationId', 'in', chunk)
        );
        const snapshot = await getDocs(q);
        snapshot.forEach((d) => {
          totalUnread += d.data().unreadCount || 0;
        });
      }
      return { success: true, data: totalUnread };
    } catch (error: any) {
      console.error('Error getting unread count all orgs:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to get unread count' };
    }
  },

  /**
   * Subscribe to conversations for a user across multiple organizations.
   * Returns single unsubscribe function.
   */
  subscribeToConversationsAllOrgs: (
    userId: string,
    organizationIds: string[],
    callback: (conversations: Conversation[]) => void
  ) => {
    if (!organizationIds.length) {
      callback([]);
      return () => {};
    }

    const participantsRef = collection(db, 'conversationParticipants');
    const chunkSize = 10;
    const unsubscribes: (() => void)[] = [];
    const conversationIdsBySnapshot = new Map<number, Set<string>>();
    let snapshotIndex = 0;

    const fetchAndEmit = async (conversationIds: Set<string>) => {
      if (conversationIds.size === 0) {
        callback([]);
        return;
      }
      const conversationPromises = Array.from(conversationIds).map(async (convId) => {
        const convRef = doc(db, 'conversations', convId);
        const convSnap = await getDoc(convRef);
        if (convSnap.exists()) return { id: convSnap.id, ...convSnap.data() } as Conversation;
        return null;
      });
      const conversations = await Promise.all(conversationPromises);
      const valid = conversations.filter((c): c is Conversation => c !== null);
      valid.sort((a, b) => {
        const aTime = a.updatedAt?.toMillis?.() || (a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0) || (a.lastMessage?.sentAt?.toMillis?.() || 0);
        const bTime = b.updatedAt?.toMillis?.() || (b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0) || (b.lastMessage?.sentAt?.toMillis?.() || 0);
        return bTime - aTime;
      });
      callback(valid);
    };

    const mergeAndEmit = () => {
      const allIds = new Set<string>();
      conversationIdsBySnapshot.forEach((set) => set.forEach((id) => allIds.add(id)));
      fetchAndEmit(allIds);
    };

    for (let i = 0; i < organizationIds.length; i += chunkSize) {
      const chunk = organizationIds.slice(i, i + chunkSize);
      const idx = snapshotIndex++;
      const q = query(
        participantsRef,
        where('userId', '==', userId),
        where('organizationId', 'in', chunk)
      );
      const unsub = onSnapshot(
        q,
        (snapshot) => {
          const ids = new Set<string>();
          snapshot.docs.forEach((d) => {
            const data = d.data();
            if (!data.deletedAt) ids.add(data.conversationId);
          });
          conversationIdsBySnapshot.set(idx, ids);
          mergeAndEmit();
        },
        (err) => console.error('Error in subscribeToConversationsAllOrgs:', err)
      );
      unsubscribes.push(unsub);
    }

    return () => {
      unsubscribes.forEach((u) => u());
    };
  },

  /**
   * Subscribe to total unread count for a user across multiple organizations.
   * Returns unsubscribe function.
   */
  subscribeToUnreadCountAllOrgs: (
    userId: string,
    organizationIds: string[],
    callback: (unreadCount: number) => void
  ) => {
    if (!organizationIds.length) {
      callback(0);
      return () => {};
    }

    const participantsRef = collection(db, 'conversationParticipants');
    const chunkSize = 10;
    const unsubscribes: (() => void)[] = [];
    const unreadByChunk = new Map<number, number>();
    let chunkIndex = 0;

    const emitTotal = () => {
      let total = 0;
      unreadByChunk.forEach((n) => (total += n));
      callback(total);
    };

    for (let i = 0; i < organizationIds.length; i += chunkSize) {
      const chunk = organizationIds.slice(i, i + chunkSize);
      const idx = chunkIndex++;
      const q = query(
        participantsRef,
        where('userId', '==', userId),
        where('organizationId', 'in', chunk)
      );
      const unsub = onSnapshot(
        q,
        (snapshot) => {
          let sum = 0;
          snapshot.docs.forEach((d) => {
            sum += d.data().unreadCount || 0;
          });
          unreadByChunk.set(idx, sum);
          emitTotal();
        },
        (err) => console.error('Error in subscribeToUnreadCountAllOrgs:', err)
      );
      unsubscribes.push(unsub);
    }

    return () => unsubscribes.forEach((u) => u());
  },

  /**
   * Add member to conversation (conversation admin or system admin/manager only)
   * Works for all non-direct conversation types (group, task, contract, checklist, form)
   */
  addGroupMember: async (
    conversationId: string,
    userId: string,
    addedBy: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      // Verify conversation exists
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        return { success: false, error: 'Conversation not found' };
      }

      const conversation = { id: conversationSnap.id, ...conversationSnap.data() } as Conversation;

      // Only allow adding members to non-direct conversations
      if (conversation.type === 'direct') {
        return { success: false, error: 'Cannot add members to direct conversations' };
      }

      // Verify addedBy is a conversation admin OR has system role admin/manager
      const adminParticipantId = `${conversationId}_${addedBy}`;
      const adminParticipantRef = doc(db, 'conversationParticipants', adminParticipantId);
      const adminParticipantSnap = await getDoc(adminParticipantRef);
      
      const isConversationAdmin = adminParticipantSnap.exists() && adminParticipantSnap.data().role === 'admin';
      
      // Check if user has system role admin or manager using organization-specific roles
      let hasSystemAdminOrManager = false;
      if (!isConversationAdmin) {
        const { getUserSystemRoleForOrganization } = await import('@/utils/roleHelpers');
        const addedByUserRef = doc(db, 'users', addedBy);
        const addedByUserSnap = await getDoc(addedByUserRef);
        if (addedByUserSnap.exists()) {
          const systemRole = getUserSystemRoleForOrganization(addedByUserSnap.data(), conversation.organizationId);
          hasSystemAdminOrManager = systemRole === 'admin' || systemRole === 'manager';
          
          // Fallback to legacy role check if organization-specific role not found
          if (!hasSystemAdminOrManager) {
            const userRoles = await fetchUserRoles(addedBy);
            hasSystemAdminOrManager = userRoles.isSystemManager;
          }
        }
      }

      if (!isConversationAdmin && !hasSystemAdminOrManager) {
        return { success: false, error: 'Only conversation admins or users with admin/manager system roles can add members' };
      }

      // Add user to participants if not already there
      if (!conversation.participants.includes(userId)) {
        await updateDoc(conversationRef, {
          participants: arrayUnion(userId),
          updatedAt: serverTimestamp(),
        });
        // Update conversation object to include new participant
        conversation.participants = [...conversation.participants, userId];
      }

      // Create or update participant document
      await createConversationParticipant(
        conversationId,
        userId,
        conversation.organizationId,
        'member'
      );

      // Sync all participant documents as a safeguard
      await syncConversationParticipantDocuments(conversation);

      return { success: true };
    } catch (error: any) {
      console.error('Error adding group member:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to add member' };
    }
  },

  /**
   * Remove member from conversation (conversation admin or system admin/manager only)
   * Works for all non-direct conversation types (group, task, contract, checklist, form)
   */
  removeGroupMember: async (
    conversationId: string,
    userId: string,
    removedBy: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      // Verify conversation exists
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        return { success: false, error: 'Conversation not found' };
      }

      const conversation = { id: conversationSnap.id, ...conversationSnap.data() } as Conversation;

      // Only allow removing members from non-direct conversations
      if (conversation.type === 'direct') {
        return { success: false, error: 'Cannot remove members from direct conversations' };
      }

      // Verify removedBy is a conversation admin OR has system role admin/manager
      const adminParticipantId = `${conversationId}_${removedBy}`;
      const adminParticipantRef = doc(db, 'conversationParticipants', adminParticipantId);
      const adminParticipantSnap = await getDoc(adminParticipantRef);
      
      const isConversationAdmin = adminParticipantSnap.exists() && adminParticipantSnap.data().role === 'admin';
      
      // Check if user has system role admin or manager using organization-specific roles
      let hasSystemAdminOrManager = false;
      if (!isConversationAdmin) {
        const { getUserSystemRoleForOrganization } = await import('@/utils/roleHelpers');
        const removedByUserRef = doc(db, 'users', removedBy);
        const removedByUserSnap = await getDoc(removedByUserRef);
        if (removedByUserSnap.exists()) {
          const systemRole = getUserSystemRoleForOrganization(removedByUserSnap.data(), conversation.organizationId);
          hasSystemAdminOrManager = systemRole === 'admin' || systemRole === 'manager';
          
          // Fallback to legacy role check if organization-specific role not found
          if (!hasSystemAdminOrManager) {
            const userRoles = await fetchUserRoles(removedBy);
            hasSystemAdminOrManager = userRoles.isSystemManager;
          }
        }
      }

      if (!isConversationAdmin && !hasSystemAdminOrManager) {
        return { success: false, error: 'Only conversation admins or users with admin/manager system roles can remove members' };
      }

      // Remove user from participants
      await updateDoc(conversationRef, {
        participants: arrayRemove(userId),
        updatedAt: serverTimestamp(),
      });

      // Delete participant document
      const participantId = `${conversationId}_${userId}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);
      await deleteDoc(participantRef);

      return { success: true };
    } catch (error: any) {
      console.error('Error removing group member:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to remove member' };
    }
  },

  /**
   * Update group info (name, description) - admin only
   */
  updateGroupInfo: async (
    conversationId: string,
    updates: { name?: string; description?: string },
    updatedBy: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      // Verify conversation exists and is a group
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        return { success: false, error: 'Conversation not found' };
      }

      const conversation = { id: conversationSnap.id, ...conversationSnap.data() } as Conversation;

      if (conversation.type !== 'group') {
        return { success: false, error: 'Can only update group conversations' };
      }

      // Verify updatedBy is an admin
      const adminParticipantId = `${conversationId}_${updatedBy}`;
      const adminParticipantRef = doc(db, 'conversationParticipants', adminParticipantId);
      const adminParticipantSnap = await getDoc(adminParticipantRef);

      if (!adminParticipantSnap.exists() || adminParticipantSnap.data().role !== 'admin') {
        return { success: false, error: 'Only group admins can update group info' };
      }

      // Prepare update data
      const updateData: any = {
        updatedAt: serverTimestamp(),
      };

      if (updates.name !== undefined) {
        updateData.name = updates.name.trim();
      }

      if (updates.description !== undefined) {
        updateData.description = updates.description?.trim() || null;
      }

      await updateDoc(conversationRef, updateData);

      return { success: true };
    } catch (error: any) {
      console.error('Error updating group info:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to update group info' };
    }
  },

  /**
   * Get conversation by ID
   */
  getConversation: async (conversationId: string): Promise<MessagingResult<Conversation>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        return { success: false, error: 'Conversation not found' };
      }

      const conversation = { id: conversationSnap.id, ...conversationSnap.data() } as Conversation;
      
      // Sync participant documents to fix orphaned participants
      // This ensures all participants in the array have corresponding participant documents
      // This fixes the bug where users show in participants array but don't see conversation on mobile
      await syncConversationParticipantDocuments(conversation);

      return {
        success: true,
        data: conversation,
      };
    } catch (error: any) {
      console.error('Error getting conversation:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to get conversation' };
    }
  },

  /**
   * Get conversation participant info
   */
  getConversationParticipant: async (
    conversationId: string,
    userId: string
  ): Promise<MessagingResult<ConversationParticipant>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const participantId = `${conversationId}_${userId}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);
      const participantSnap = await getDoc(participantRef);

      if (!participantSnap.exists()) {
        return { success: false, error: 'Participant not found' };
      }

      return {
        success: true,
        data: { id: participantSnap.id, ...participantSnap.data() } as ConversationParticipant,
      };
    } catch (error: any) {
      console.error('Error getting conversation participant:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to get participant' };
    }
  },

  // ============ REAL-TIME LISTENERS ============

  /**
   * Subscribe to conversations for a user in an organization
   * Returns unsubscribe function
   */
  subscribeToConversations: (
    userId: string,
    organizationId: string,
    callback: (conversations: Conversation[]) => void
  ) => {
    const participantsRef = collection(db, 'conversationParticipants');
    const participantsQuery = query(
      participantsRef,
      where('userId', '==', userId),
      where('organizationId', '==', organizationId)
    );

    const conversationMap = new Map<string, Conversation>();

      return onSnapshot(
      participantsQuery,
      async (participantsSnapshot) => {
        const conversationIds = new Set<string>();
        
        participantsSnapshot.forEach((doc) => {
          const data = doc.data();
          // Only include conversations that are not deleted
          if (!data.deletedAt) {
            conversationIds.add(data.conversationId);
          }
        });

        // Fetch all conversations
        const conversationPromises = Array.from(conversationIds).map(async (convId) => {
          const convRef = doc(db, 'conversations', convId);
          const convSnap = await getDoc(convRef);
          if (convSnap.exists()) {
            return { id: convSnap.id, ...convSnap.data() } as Conversation;
          }
          return null;
        });

        const conversations = await Promise.all(conversationPromises);
        const validConversations = conversations.filter((c): c is Conversation => c !== null);

        // Sort by updatedAt (most recent first)
        validConversations.sort((a, b) => {
          const aTime = a.updatedAt?.toMillis?.() || 
                        (a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0) ||
                        (a.lastMessage?.sentAt?.toMillis?.() || 0);
          const bTime = b.updatedAt?.toMillis?.() || 
                        (b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0) ||
                        (b.lastMessage?.sentAt?.toMillis?.() || 0);
          return bTime - aTime;
        });

        callback(validConversations);
      },
      (error) => {
        console.error('Error in conversations subscription:', error);
      }
    );
  },

  /**
   * Subscribe to messages in a conversation
   * Returns unsubscribe function
   */
  subscribeToMessages: (
    conversationId: string,
    callback: (messages: Message[]) => void,
    limitCount: number = 50
  ) => {
    const messagesRef = collection(db, 'messages');
    const messagesQuery = query(
      messagesRef,
      where('conversationId', '==', conversationId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(
      messagesQuery,
      (messagesSnapshot) => {
        const messages: Message[] = [];

        messagesSnapshot.forEach((doc) => {
          const data = doc.data();
          const message = { id: doc.id, ...data } as Message;
          
          // Debug: log messages with @ to check mentions
          if (message.text && message.text.includes('@')) {
          }
          
          messages.push(message);
        });

        // Reverse to show oldest first
        messages.reverse();

        callback(messages);
      },
      (error) => {
        console.error('Error in messages subscription:', error);
      }
    );
  },

  /**
   * Subscribe to unread count for a user in an organization
   * Returns unsubscribe function
   */
  subscribeToUnreadCount: (
    userId: string,
    organizationId: string,
    callback: (unreadCount: number) => void
  ) => {
    const participantsRef = collection(db, 'conversationParticipants');
    const participantsQuery = query(
      participantsRef,
      where('userId', '==', userId),
      where('organizationId', '==', organizationId)
    );

    return onSnapshot(
      participantsQuery,
      (participantsSnapshot) => {
        let totalUnread = 0;

        participantsSnapshot.forEach((doc) => {
          const unreadCount = doc.data().unreadCount || 0;
          totalUnread += unreadCount;
        });

        callback(totalUnread);
      },
      (error) => {
        console.error('Error in unread count subscription:', error);
      }
    );
  },

  /**
   * Add a reaction to a message
   */
  addReaction: async (
    messageId: string,
    emoji: string,
    userId: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const messageRef = doc(db, 'messages', messageId);
      const messageSnap = await getDoc(messageRef);

      if (!messageSnap.exists()) {
        return { success: false, error: 'Message not found' };
      }

      const messageData = messageSnap.data();
      const reactions: MessageReaction[] = messageData.reactions || [];

      // Check if user already reacted with this emoji
      const existingReactionIndex = reactions.findIndex(
        (r) => r.userId === userId && r.emoji === emoji
      );

      if (existingReactionIndex >= 0) {
        // User already reacted with this emoji, remove it
        reactions.splice(existingReactionIndex, 1);
      } else {
        // Remove any existing reaction from this user with different emoji
        const userReactionIndex = reactions.findIndex((r) => r.userId === userId);
        if (userReactionIndex >= 0) {
          reactions.splice(userReactionIndex, 1);
        }

        // Get user info for reaction
        const userInfo = await getUserDisplayInfo(userId);

        // Add new reaction (can't use serverTimestamp in arrays, using client timestamp)
        reactions.push({
          emoji,
          userId,
          userName: userInfo.name,
          createdAt: Timestamp.fromDate(new Date()),
        });
      }

      await updateDoc(messageRef, {
        reactions: reactions.length > 0 ? reactions : null,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error adding reaction:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to add reaction' };
    }
  },

  /**
   * Remove a reaction from a message
   */
  removeReaction: async (
    messageId: string,
    userId: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const messageRef = doc(db, 'messages', messageId);
      const messageSnap = await getDoc(messageRef);

      if (!messageSnap.exists()) {
        return { success: false, error: 'Message not found' };
      }

      const messageData = messageSnap.data();
      const reactions: MessageReaction[] = messageData.reactions || [];

      // Remove user's reaction
      const filteredReactions = reactions.filter((r) => r.userId !== userId);

      await updateDoc(messageRef, {
        reactions: filteredReactions.length > 0 ? filteredReactions : null,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error removing reaction:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to remove reaction' };
    }
  },

  /**
   * Forward a message to another conversation
   */
  forwardMessage: async (
    messageId: string,
    targetConversationId: string,
    sentBy: string,
    optionalMessage?: string
  ): Promise<MessagingResult<Message>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      // Get the original message
      const messageRef = doc(db, 'messages', messageId);
      const messageSnap = await getDoc(messageRef);

      if (!messageSnap.exists()) {
        return { success: false, error: 'Message not found' };
      }

      const originalMessage = { id: messageSnap.id, ...messageSnap.data() } as Message;

      // Get target conversation to validate
      const targetConversationRef = doc(db, 'conversations', targetConversationId);
      const targetConversationSnap = await getDoc(targetConversationRef);

      if (!targetConversationSnap.exists()) {
        return { success: false, error: 'Target conversation not found' };
      }

      const targetConversation = {
        id: targetConversationSnap.id,
        ...targetConversationSnap.data(),
      } as Conversation;

      // Validate user is a participant in target conversation
      if (!targetConversation.participants.includes(sentBy)) {
        return { success: false, error: 'You are not a participant in the target conversation' };
      }

      // Get user display info
      const userInfo = await getUserDisplayInfo(sentBy);

      // Get original conversation name for forward context
      const originalConversationRef = doc(db, 'conversations', originalMessage.conversationId);
      const originalConversationSnap = await getDoc(originalConversationRef);
      let originalConversationName: string | undefined;
      
      if (originalConversationSnap.exists()) {
        const originalConv = originalConversationSnap.data() as Conversation;
        originalConversationName = originalConv.name || originalConv.participants.join(', ');
      }

      // Create forwarded message
      const messagesRef = collection(db, 'messages');
      
      // Determine text for forwarded message
      let forwardedText = originalMessage.text || '';
      if (optionalMessage && optionalMessage.trim() && !originalMessage.text && originalMessage.attachments && originalMessage.attachments.length > 0) {
        // If original has only attachments and optional message provided, use optional as text
        forwardedText = optionalMessage.trim();
      }
      
      const forwardedMessageData: any = {
        conversationId: targetConversationId,
        sentBy,
        sentByName: userInfo.name,
        text: forwardedText,
        organizationId: targetConversation.organizationId,
        forwardedFrom: {
          conversationId: originalMessage.conversationId,
          conversationName: originalConversationName,
          messageId: originalMessage.id,
          originalSentBy: originalMessage.sentBy,
          originalSentByName: originalMessage.sentByName,
        },
        createdAt: serverTimestamp(),
      };

      // Include attachments if present
      if (originalMessage.attachments && originalMessage.attachments.length > 0) {
        forwardedMessageData.attachments = originalMessage.attachments;
      }

      const forwardedMessageRef = await addDoc(messagesRef, forwardedMessageData);
      
      // If optional message is provided and original message has text, send optional message as separate message
      if (optionalMessage && optionalMessage.trim() && originalMessage.text) {
        const optionalMessageData: any = {
          conversationId: targetConversationId,
          sentBy,
          sentByName: userInfo.name,
          text: optionalMessage.trim(),
          organizationId: targetConversation.organizationId,
          createdAt: serverTimestamp(),
        };
        await addDoc(messagesRef, optionalMessageData);
      }

      // Update target conversation lastMessage (use optional message if provided)
      const lastMessageText = (optionalMessage && optionalMessage.trim()) 
        ? optionalMessage.trim()
        : (originalMessage.text || '📎 Attachment');
      
      await updateDoc(targetConversationRef, {
        lastMessage: {
          text: lastMessageText,
          sentBy,
          sentByName: userInfo.name,
          sentAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });

      // Update unread counts for all participants except sender
      const batch = writeBatch(db);
      const participantPromises = targetConversation.participants
        .filter(userId => userId !== sentBy)
        .map(async (userId) => {
          const participantId = `${targetConversationId}_${userId}`;
          const participantRef = doc(db, 'conversationParticipants', participantId);
          const participantSnap = await getDoc(participantRef);
          
          if (participantSnap.exists()) {
            const currentCount = participantSnap.data().unreadCount || 0;
            batch.update(participantRef, {
              unreadCount: currentCount + 1,
            });
          } else {
            // Create participant if doesn't exist (edge case)
            batch.set(participantRef, {
              conversationId: targetConversationId,
              userId,
              organizationId: targetConversation.organizationId,
              unreadCount: 1,
              joinedAt: serverTimestamp(),
              role: 'member',
            });
          }
        });

      await Promise.all(participantPromises);
      await batch.commit();

      const forwardedMessage: Message = {
        id: forwardedMessageRef.id,
        ...forwardedMessageData,
      };

      return { success: true, data: forwardedMessage };
    } catch (error: any) {
      console.error('Error forwarding message:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to forward message' };
    }
  },

  /**
   * Mute notifications for a conversation
   */
  muteConversation: async (
    conversationId: string,
    userId: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const participantId = `${conversationId}_${userId}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);
      
      await updateDoc(participantRef, {
        muted: true,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error muting conversation:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to mute conversation' };
    }
  },

  /**
   * Unmute notifications for a conversation
   */
  unmuteConversation: async (
    conversationId: string,
    userId: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const participantId = `${conversationId}_${userId}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);
      
      await updateDoc(participantRef, {
        muted: false,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error unmuting conversation:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to unmute conversation' };
    }
  },

  /**
   * Delete a conversation (soft delete - marks as deleted for the user)
   * If a new message arrives, the conversation will be restored
   */
  deleteConversation: async (
    conversationId: string,
    userId: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const participantId = `${conversationId}_${userId}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);
      
      await updateDoc(participantRef, {
        deletedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error deleting conversation:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to delete conversation' };
    }
  },

  /**
   * Restore a deleted conversation (removes deletedAt timestamp)
   * This is automatically called when a new message arrives in a deleted conversation
   */
  restoreConversation: async (
    conversationId: string,
    userId: string
  ): Promise<MessagingResult<void>> => {
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      return firebaseCheck;
    }

    try {
      const participantId = `${conversationId}_${userId}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);
      
      // Use updateDoc with FieldValue.delete() to remove the field
      const { deleteField } = await import('firebase/firestore');
      await updateDoc(participantRef, {
        deletedAt: deleteField(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error restoring conversation:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to restore conversation' };
    }
  },
};

